import React, { useEffect, useMemo, useState } from "react";
import styles from "./campaignOrders/campaignItem/CampaignItem.module.scss";
import { Skeleton } from "@bale/ui";
import { getFileLinkThunk } from "core/file/file.thunk";
import { useRequest } from "hooks/useRequest";
import { AdSpot, ApiAdRecord } from "core/orderAd/orderAd.entity";
import useIsLargeScreen from "../hooks/useIsDesktop";

interface Props {
  spot: ApiAdRecord["spot"];
  pic_url: ApiAdRecord["pic_url"];
  id: ApiAdRecord["id"];
}

const AdThumbnail = ({ spot, pic_url, id }: Props) => {
  const { sendRequest: getFileLink, loading: LoadingUrl } = useRequest(getFileLinkThunk);
  const [imageUrl, setImageUrl] = useState<string | undefined>();
  const [isImageLoaded, setImageLoaded] = useState(false);
  const [errorOnImageLoad, setErrorOnImageLoad] = useState(false);
  const isDesktop = useIsLargeScreen(1400);

  const thumbnail = useMemo(() => {
    return (
      <>
        {!isImageLoaded && (
          <Skeleton
            type={spot === AdSpot.AdSpotSponsoredMessage ? "rectangular" : "circular"}
            height={spot === AdSpot.AdSpotSponsoredMessage ? "38px" : "50px"}
            width={spot === AdSpot.AdSpotSponsoredMessage ? "68px" : "50px"}
            className={`${spot === AdSpot.AdSpotSponsoredMessage ? styles.sponsoredPic : styles.dialogPic}`}
            id={id}
          />
        )}
        {imageUrl && (
          <img
            className={
              isImageLoaded
                ? `${spot === AdSpot.AdSpotSponsoredMessage ? styles.sponsoredPic : styles.dialogPic}`
                : undefined
            }
            style={!isImageLoaded ? { opacity: 0 } : undefined}
            src={imageUrl}
            onLoad={() => setImageLoaded(true)}
            alt={isImageLoaded ? "advertisement thumbnail" : undefined}
            loading="lazy"
          />
        )}
      </>
    );
  }, [LoadingUrl, isImageLoaded, imageUrl, spot]);

  useEffect(() => {
    if (pic_url && IntersectionObserver) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            getFileLink({
              file_id: pic_url,
              onSuccess: (response) => setImageUrl(response?.file_link),
              onError: () => setErrorOnImageLoad(true),
            });
            observer.disconnect();
          }
        },
        { threshold: 0.1 }
      );

      const element = document.getElementById(id);
      if (element) {
        observer.observe(element);
      }

      return () => {
        observer.disconnect();
      };
    }
  }, [pic_url]);

  return pic_url && !errorOnImageLoad ? thumbnail : isDesktop ? <p>-</p> : null;
};

export default AdThumbnail;
